import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import GUI from 'lil-gui'

import vertexCommon from './shaders/vertex/_common.glsl'
import vertexMain from './shaders/vertex/_main.glsl'
import fragmentCommon from './shaders/fragment/_common.glsl'
import fragmentMain from './shaders/fragment/_main.glsl'

/**
 * Shader library
 * Every shaders/{vertex,fragment}/N-name.glsl (not starting with _) shows up in the GUI.
 * Drop a new file in the folder to add an effect; the number sets its position in the list.
 */
const toLibrary = (modules) =>
{
    const library = {}
    Object.keys(modules)
        .filter((path) => !path.split('/').pop().startsWith('_'))
        .sort((a, b) => parseInt(a.split('/').pop()) - parseInt(b.split('/').pop()))
        .forEach((path) =>
        {
            // ./shaders/fragment/1-rgbSplit.glsl -> "Rgb split"
            const name = path.split('/').pop().replace(/^\d+-/, '').replace(/\.glsl$/, '')
            const label = name.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
            library[label.charAt(0).toUpperCase() + label.slice(1)] = modules[path]
        })
    return library
}

const VERTEX_EFFECTS = toLibrary(import.meta.glob('./shaders/vertex/*.glsl', { eager: true, import: 'default' }))
const FRAGMENT_EFFECTS = toLibrary(import.meta.glob('./shaders/fragment/*.glsl', { eager: true, import: 'default' }))

const buildVertexShader = (effect) => `${vertexCommon}\n${effect}\n${vertexMain}`
const buildFragmentShader = (effect) => `${fragmentCommon}\n${effect}\n${fragmentMain}`

const gui = new GUI()
const canvas = document.querySelector('canvas.webgl')
const video = document.querySelector('video.webcam')
const status = document.querySelector('.status')
const scene = new THREE.Scene()

/**
 * Webcam
 */
const showStatus = (message) =>
{
    status.textContent = message
    status.hidden = !message
}

const videoTexture = new THREE.VideoTexture(video)

const startWebcam = async () =>
{
    if(!navigator.mediaDevices?.getUserMedia)
    {
        showStatus('Webcam not available. It needs https or localhost.')
        return
    }
    try
    {
        showStatus('Waiting for webcam permission…')
        video.srcObject = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false
        })
        await video.play()
        showStatus('')
    }
    catch(error)
    {
        showStatus(`Could not open webcam: ${error.message}`)
    }
}

// Keep the plane at the webcam's aspect ratio once its real size is known
video.addEventListener('loadedmetadata', () =>
{
    params.aspect = video.videoWidth / video.videoHeight
    material.uniforms.uResolution.value.set(video.videoWidth, video.videoHeight)
    rebuildGeometry()
})

/**
 * Surface
 */
const params = {
    effect: Object.values(FRAGMENT_EFFECTS)[0],
    mesh: Object.values(VERTEX_EFFECTS)[0],
    shape: 'Plane',
    curvature: 0.5,
    segments: 128,
    points: false,
    aspect: 16 / 9
}

const material = new THREE.ShaderMaterial({
    vertexShader: buildVertexShader(params.mesh),
    fragmentShader: buildFragmentShader(params.effect),
    side: THREE.DoubleSide,
    uniforms: {
        uTexture: { value: videoTexture },
        uResolution: { value: new THREE.Vector2(1280, 720) },
        uTime: { value: 0 },
        uMirror: { value: true },
        uAmount: { value: 0.5 },
        uPixelSize: { value: 12 },
        uLevels: { value: 4 },
        uStrength: { value: 0.2 },
        uFrequency: { value: 6 },
        uSpeed: { value: 1 },
        uSegments: { value: params.segments },
        uPointSize: { value: 6 },
        uPixelRatio: { value: Math.min(window.devicePixelRatio, 2) }
    }
})

/**
 * Shapes the webcam is mapped onto.
 * Vertex effects displace along `normal`, so every shape needs sensible normals.
 */
// Plane bent around a vertical axis, like a curved monitor.
// curvature 1 = full cylinder towards the camera, -1 = bent away, 0 = flat.
const buildCurvedPlane = (width, height, segments, curvature) =>
{
    const geometry = new THREE.PlaneGeometry(width, height, segments, segments)
    if(Math.abs(curvature) < 0.001)
        return geometry

    const radius = width / (curvature * Math.PI * 2)
    const position = geometry.attributes.position
    const normal = geometry.attributes.normal
    for(let i = 0; i < position.count; i++)
    {
        const angle = position.getX(i) / radius
        position.setX(i, radius * Math.sin(angle))
        position.setZ(i, radius * (1 - Math.cos(angle)))
        normal.setXYZ(i, -Math.sin(angle), 0, Math.cos(angle))
    }
    return geometry
}

const SHAPES = {
    'Plane': (s) => new THREE.PlaneGeometry(2 * params.aspect, 2, s, s),
    'Curved plane': (s) => buildCurvedPlane(2 * params.aspect, 2, s, params.curvature),
    // thetaStart / phiStart rotate the uv seam to the back so the image centre faces the camera
    'Cylinder': (s) => new THREE.CylinderGeometry(1, 1, 2, s, s, true, -Math.PI),
    'Sphere': (s) => new THREE.SphereGeometry(1.2, s, Math.max(2, s / 2), -Math.PI / 2),
    'Torus': (s) => new THREE.TorusGeometry(0.9, 0.4, Math.max(3, s / 2), s),
    'Box': (s) => new THREE.BoxGeometry(1.5, 1.5, 1.5, Math.max(1, s / 4), Math.max(1, s / 4), Math.max(1, s / 4))
}

const buildGeometry = () => SHAPES[params.shape](params.segments)

// Mesh and Points share geometry + material; only one is visible
const geometry = buildGeometry()
const mesh = new THREE.Mesh(geometry, material)
const points = new THREE.Points(geometry, material)
points.visible = false
scene.add(mesh, points)

const rebuildGeometry = () =>
{
    mesh.geometry.dispose()
    mesh.geometry = points.geometry = buildGeometry()
    material.uniforms.uSegments.value = params.segments
}

/**
 * GUI
 */
const fragmentFolder = gui.addFolder('Fragment (image)')
// Swapping source + needsUpdate makes three recompile the program
fragmentFolder.add(params, 'effect', FRAGMENT_EFFECTS).onChange((source) =>
{
    material.fragmentShader = buildFragmentShader(source)
    material.needsUpdate = true
})
fragmentFolder.add(material.uniforms.uAmount, 'value', 0, 1, 0.01).name('amount')
fragmentFolder.add(material.uniforms.uPixelSize, 'value', 1, 64, 1).name('pixel size')
fragmentFolder.add(material.uniforms.uLevels, 'value', 2, 16, 1).name('posterize levels')
fragmentFolder.add(material.uniforms.uMirror, 'value').name('mirror')

const shapeFolder = gui.addFolder('Shape')
shapeFolder.add(params, 'shape', Object.keys(SHAPES)).onChange(() =>
{
    curvatureController.show(params.shape === 'Curved plane')
    rebuildGeometry()
})
const curvatureController = shapeFolder.add(params, 'curvature', -1, 1, 0.01).onChange(rebuildGeometry).hide()
shapeFolder.add(params, 'segments', 1, 512, 1).onFinishChange(rebuildGeometry)

const vertexFolder = gui.addFolder('Vertex (mesh)')
vertexFolder.add(params, 'mesh', VERTEX_EFFECTS).onChange((source) =>
{
    material.vertexShader = buildVertexShader(source)
    material.needsUpdate = true
})
vertexFolder.add(material.uniforms.uStrength, 'value', 0, 1, 0.01).name('strength')
vertexFolder.add(material.uniforms.uFrequency, 'value', 0, 20, 0.1).name('frequency')
vertexFolder.add(material.uniforms.uSpeed, 'value', 0, 5, 0.01).name('speed')
vertexFolder.add(material, 'wireframe')
vertexFolder.add(params, 'points').name('render as points').onChange((value) =>
{
    points.visible = value
    mesh.visible = !value
})
vertexFolder.add(material.uniforms.uPointSize, 'value', 1, 20, 0.1).name('point size')

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    material.uniforms.uPixelRatio.value = Math.min(window.devicePixelRatio, 2)
})

/**
 * Camera
 */
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.set(0, 0, 1.8)
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    material.uniforms.uTime.value = clock.getElapsedTime()

    controls.update()
    renderer.render(scene, camera)

    window.requestAnimationFrame(tick)
}

tick()
startWebcam()
